import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, TextField, Button, Alert, Link } from '@mui/material';
import { PublicLayout } from '../layouts/PublicLayout';
import { authService } from '../services/api/authService';

export function PasswordResetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);

    try {
      await authService.requestPasswordReset(email);
      setMessage('パスワードリセット用のメールを送信しました。メールをご確認ください。');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'エラーが発生しました';
      // セキュリティ上、メールアドレスの存在を明らかにしないメッセージを表示
      if (errorMessage.includes('not found') || errorMessage.includes('見つかりません')) {
        setMessage('パスワードリセット用のメールを送信しました。メールをご確認ください。');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    setIsLoading(true);

    try {
      await authService.resetPassword(token!, newPassword);
      setMessage('パスワードを変更しました。ログインページからログインしてください。');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'エラーが発生しました';
      // ユーザーに分かりやすいエラーメッセージに変換
      if (errorMessage.includes('expired') || errorMessage.includes('期限')) {
        setError('パスワードリセットリンクの有効期限が切れています。再度リセットをリクエストしてください。');
      } else if (errorMessage.includes('invalid') || errorMessage.includes('無効')) {
        setError('無効なリセットリンクです。再度リセットをリクエストしてください。');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (token) {
    return (
      <PublicLayout title="新しいパスワードを設定">
        <Box component="form" onSubmit={handleResetPassword} noValidate>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="新しいパスワード"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="8文字以上、英数字混在"
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="パスワード確認"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={isLoading}
          >
            {isLoading ? '処理中...' : 'パスワードを変更'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate('/login')}
            >
              ログインページに戻る
            </Link>
          </Box>
        </Box>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout title="パスワードリセット">
      <Box component="form" onSubmit={handleRequestReset} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="登録済みメールアドレス"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={isLoading}
        >
          {isLoading ? '送信中...' : 'リセットメールを送信'}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => navigate('/login')}
          >
            ログインページに戻る
          </Link>
        </Box>
      </Box>
    </PublicLayout>
  );
}
